import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";
import streetIcon from "../../../assets/icons/street.png";
import undergroundIcon from "../../../assets/icons/underground.png";
import coveredIcon from "../../../assets/icons/covered.png";

// Map parking types to their icons
const parkingTypeIcons = {
  street: streetIcon,
  underground: undergroundIcon,
  "enclosed above-ground": coveredIcon,
};

export const ExistingParkingPolygons = ({
  parkings,
  editingParking,
  onPolygonEdit,
  onMarkerEdit,
  zoomToParking,
  isEditing,
}) => {
  const map = useMap();
  const polygonsRef = useRef([]);
  const markersRef = useRef([]);
  const editingPolygonRef = useRef(null);
  const editingMarkerRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google) return;

    // Clear all existing polygons and markers
    polygonsRef.current.forEach((polygon) => {
      polygon.setMap(null);
      window.google.maps.event.clearInstanceListeners(polygon);
      if (polygon.getPath()) {
        window.google.maps.event.clearInstanceListeners(polygon.getPath());
      }
    });
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
      window.google.maps.event.clearInstanceListeners(marker);
    });

    polygonsRef.current = [];
    markersRef.current = [];
    editingPolygonRef.current = null;
    editingMarkerRef.current = null;

    parkings.forEach((parking) => {
      const isCurrentlyEditing = editingParking?.id === parking.id;

      // Create polygon only if coordinates exist
      if (parking.coordinates && parking.coordinates.length > 0) {
        // Use editingParking coordinates if this is the parking being edited, otherwise use parking coordinates
        const coordinatesToUse =
          isCurrentlyEditing && editingParking
            ? editingParking.coordinates
            : parking.coordinates;

        // Create polygon
        const polygon = new window.google.maps.Polygon({
          paths: coordinatesToUse,
          strokeColor: isCurrentlyEditing ? "#ff4d4f" : "#722ed1",
          strokeOpacity: 0.8,
          strokeWeight: isCurrentlyEditing ? 3 : 2,
          fillColor: isCurrentlyEditing ? "#ff7875" : "#722ed1",
          fillOpacity: 0.2,
          editable: isCurrentlyEditing && isEditing,
          draggable: false,
          clickable: isCurrentlyEditing && isEditing,
          map: map,
        });

        // Store reference to editing polygon
        if (isCurrentlyEditing && isEditing) {
          editingPolygonRef.current = polygon;

          const updateCoordinates = () => {
            const path = polygon.getPath();
            const coordinates = [];
            for (let i = 0; i < path.getLength(); i++) {
              const point = path.getAt(i);
              coordinates.push({
                lat: point.lat(),
                lng: point.lng(),
              });
            }
            onPolygonEdit(coordinates);
          };

          // Add event listeners to the polygon path
          const path = polygon.getPath();
          window.google.maps.event.addListener(
            path,
            "set_at",
            updateCoordinates
          );
          window.google.maps.event.addListener(
            path,
            "insert_at",
            updateCoordinates
          );
          window.google.maps.event.addListener(
            path,
            "remove_at",
            updateCoordinates
          );
        }

        polygonsRef.current.push(polygon);
      }

      // Create marker independently (whether or not polygon exists)
      // Use editingParking marker if this is the parking being edited, otherwise use parking marker
      const markerPosition =
        isCurrentlyEditing && editingParking?.marker
          ? editingParking.marker
          : parking.marker;

      if (markerPosition) {
        const iconUrl = parkingTypeIcons[parking.parking_type] || streetIcon;

        const marker = new window.google.maps.Marker({
          position: markerPosition,
          map: map,
          title: parking.name,
          draggable: isCurrentlyEditing && isEditing,
          icon: {
            url: iconUrl,
            scaledSize: new window.google.maps.Size(30, 40),
          },
        });

        // Store reference to editing marker
        if (isCurrentlyEditing && isEditing) {
          editingMarkerRef.current = marker;

          // Add drag listener for marker position updates
          marker.addListener("dragend", (event) => {
            const newPosition = {
              lat: event.latLng.lat(),
              lng: event.latLng.lng(),
            };
            if (onMarkerEdit) {
              onMarkerEdit(newPosition);
            }
          });
        }

        // Add info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px;">
              <h3 style="margin: 0 0 8px 0;">${parking.name}</h3>
              <p style="margin: 0; color: #666;">${parking.parking_type}</p>
              ${
                isCurrentlyEditing && isEditing
                  ? '<p style="margin: 4px 0 0 0; color: #1890ff; font-size: 12px;">📍 Glissez pour déplacer</p>'
                  : ""
              }
            </div>
          `,
        });

        marker.addListener("click", () => {
          infoWindow.open(map, marker);
        });

        markersRef.current.push(marker);
      }
    });

    return () => {
      polygonsRef.current.forEach((polygon) => {
        polygon.setMap(null);
        window.google.maps.event.clearInstanceListeners(polygon);
        if (polygon.getPath()) {
          window.google.maps.event.clearInstanceListeners(polygon.getPath());
        }
      });
      markersRef.current.forEach((marker) => {
        marker.setMap(null);
        window.google.maps.event.clearInstanceListeners(marker);
      });
    };
  }, [map, parkings, editingParking, isEditing, onPolygonEdit, onMarkerEdit]);

  // Clean up editing refs when editing stops
  useEffect(() => {
    if (!isEditing) {
      editingPolygonRef.current = null;
      editingMarkerRef.current = null;
    }
  }, [isEditing]);

  // Handle zoom to parking
  useEffect(() => {
    if (
      zoomToParking &&
      map &&
      window.google &&
      ((zoomToParking.coordinates && zoomToParking.coordinates.length > 0) ||
        zoomToParking.marker)
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      if (zoomToParking.coordinates && zoomToParking.coordinates.length > 0) {
        zoomToParking.coordinates.forEach((coord) => {
          bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        });
      }
      if (zoomToParking.marker) {
        bounds.extend(
          new window.google.maps.LatLng(
            zoomToParking.marker.lat,
            zoomToParking.marker.lng
          )
        );
      }
      map.fitBounds(bounds);
    }
  }, [zoomToParking, map]);

  return null;
};
